/* External dependencies */
import { Button, Div, Text } from 'atomize';
import React, { CSSProperties } from 'react';

/* Local dependencies */
import formatMessage from '../../../intl/formatMessage';
import { CustomButton } from '../../ui/custom-button/custom-button';
import { CustomDropdown } from '../../ui/custom-dropdown/custom-dropdown';
import { MenuItem } from '../../ui/sidebar/menu-item';
import { EMPTY } from './create-consignee-good-specifications';
import { Column, Specification } from './redux/types';

type SelectOption = 'specification' | 'columnName' | 'position';

interface TableProps {
  items: Column[];
  menuItemButtonStyle: Button;
  options: Specification[];
  noContentText?: string;
  onSelectColumn: (item: Column) => void;
  onSelectOption: (item: Pick<Column, SelectOption>) => void;
}

const MAX_ROWS = 13;

export function Table(props: TableProps) {
  const {
    items,
    options,
    menuItemButtonStyle,
    noContentText,
    onSelectColumn,
    onSelectOption,
  } = props;

  const hasContent = items.length > 0;

  function getSectionValueSuffix(item: Column) {
    return (
      <CustomButton
        {...menuItemButtonStyle}
        dataTestId={`custom-button-${item.columnName}`}
        onClick={() => onSelectColumn(item)}
      />
    );
  }

  function renderTableHeader() {
    return (
      <tr>
        {items.map((item, index) => {
          const { fileName, columnName, specification } = item;
          const headerItemId = `${fileName}-${columnName}-${index}`;

          return (
            <th
              style={{ backgroundColor: 'table_cell_background' }}
              key={headerItemId}
            >
              <Div d="flex" flexDir="column">
                {options && (
                  <CustomDropdown
                    value={formatMessage(specification)}
                    menuList={options}
                    onSelect={(specification) =>
                      onSelectOption({
                        specification,
                        columnName,
                        position: index,
                      })
                    }
                  />
                )}

                <MenuItem
                  name={columnName}
                  parentName={fileName}
                  textStyle={{ whiteSpace: 'nowrap', fontWeight: '500' }}
                  suffix={() => getSectionValueSuffix(item)}
                />
              </Div>
            </th>
          );
        })}
      </tr>
    );
  }

  function renderCell(column: Column, rowIndex: number, colIndex: number) {
    const id = `cell-${rowIndex}-${colIndex}`;
    const value = column.values?.[rowIndex];

    return (
      <td key={id} style={tableDataStyle}>
        <Text maxW="200px" style={cellTextStyle} title={value}>
          {value || EMPTY}
        </Text>
      </td>
    );
  }

  function renderColumns() {
    if (items.length === 0) return null;

    return Array.from({ length: MAX_ROWS }).map((_, rowIndex) => {
      const id = `row-${rowIndex}`;

      return (
        <tr key={id}>
          {items.map((column, colIndex) =>
            renderCell(column, rowIndex, colIndex),
          )}
        </tr>
      );
    });
  }

  const NoContent = (
    <Div h="100%" d="flex" flexDir="column" justify="center">
      <Text
        tag="h1"
        textSize="display2"
        textAlign="center"
        textColor="table_cell_text_color"
      >
        {formatMessage(noContentText || 'pleaseUploadFiles')}
      </Text>
    </Div>
  );

  return (
    <Div
      id="custom-table"
      bg="table_cell_background"
      maxW="900px"
      w="100%"
      p={{ y: '15px', r: '15px' }}
    >
      <Div
        h="100%"
        overflow="auto"
        bg="input_filled_bg"
        w="100%"
        style={{
          scrollbarGutter: 'stable',
        }}
      >
        {hasContent ? (
          <table style={tableStyle}>
            <thead style={tableHeadStyle}>{renderTableHeader()}</thead>
            <tbody>{renderColumns()}</tbody>
          </table>
        ) : (
          NoContent
        )}
      </Div>
    </Div>
  );
}

const tableStyle: CSSProperties = {
  width: 'auto',
  borderCollapse: 'collapse',
  background: 'transparent',
  maxWidth: '1000px',
  padding: '5px',
};

const tableHeadStyle: CSSProperties = {
  position: 'sticky',
  top: '-1px',
  zIndex: 10,
};

const tableDataStyle: CSSProperties = {
  padding: '14px',
  overflow: 'hidden',
  color: 'black',
  fontSize: '14px',
  width: 'auto',
  maxWidth: 'none',
};

const cellTextStyle: CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
